import React from "react";
import { CheckCircle2, AlertTriangle, XCircle, FileCode2 } from "lucide-react";

const Stat = ({ value, label, cls }) => (
  <div className="flex flex-col">
    <span className={`font-display font-bold text-2xl leading-none ${cls}`}>{value}</span>
    <span className="text-[11px] uppercase tracking-wide text-zinc-400 mt-1">{label}</span>
  </div>
);

const Bar = ({ segments, total }) => (
  <div className="flex h-2 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
    {segments.map((s) =>
      s.count > 0 ? (
        <div key={s.label} className={s.cls} style={{ width: `${(s.count / total) * 100}%` }} title={`${s.label}: ${s.count}`} />
      ) : null
    )}
  </div>
);

export const ConfidenceSummary = ({ components, compValidation }) => {
  const total = components.length;
  if (total === 0) return null;

  const label = (c) => (c.confidence_label || "low").toLowerCase();
  const status = (c) => c.generation_status || "skipped";

  const high = components.filter((c) => label(c) === "high").length;
  const medium = components.filter((c) => label(c) === "medium").length;
  const low = components.filter((c) => label(c) === "low").length;

  const generated = components.filter((c) => status(c) === "generated").length;
  const partial = components.filter((c) => status(c) === "partial").length;
  const skipped = components.filter((c) => status(c) === "skipped").length;

  const renderable = components.filter((c) => compValidation[c.component_id]?.renderable).length;
  const withheld = components.filter((c) => {
    const v = compValidation[c.component_id];
    return v && !v.renderable && status(c) !== "skipped";
  }).length;

  const barSegments = [
    { count: high, cls: "bg-emerald-500", label: "high" },
    { count: medium, cls: "bg-amber-500", label: "medium" },
    { count: low, cls: "bg-red-500", label: "low" },
  ];

  return (
    <div
      data-testid="confidence-summary-card"
      className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 p-5 flex flex-col gap-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-sm uppercase tracking-[0.06em] text-zinc-500 dark:text-zinc-400">
          Confidence Overview
        </h3>
        <span className="text-xs text-zinc-400">{total} components</span>
      </div>

      <div className="flex flex-col gap-1.5">
        <Bar total={total} segments={barSegments} />
        <div className="flex items-center gap-4 text-[11px] text-zinc-500 dark:text-zinc-400">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> {high} high</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> {medium} medium</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500" /> {low} low</span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-1">
        <Stat value={generated} label="Generated" cls="text-emerald-600 dark:text-emerald-400" />
        <Stat value={partial} label="Partial" cls="text-amber-600 dark:text-amber-400" />
        <Stat value={skipped} label="Skipped" cls="text-zinc-400" />
        <Stat value={renderable} label="In LaTeX" cls="text-zinc-900 dark:text-zinc-50" />
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-zinc-500 dark:text-zinc-400 border-t border-zinc-100 dark:border-zinc-800 pt-3">
        <span className="inline-flex items-center gap-1.5"><FileCode2 className="h-3.5 w-3.5 text-zinc-900 dark:text-zinc-50" /> {renderable} patterns validated & exported</span>
        {withheld > 0 && (
          <span className="inline-flex items-center gap-1.5 text-red-600 dark:text-red-400"><AlertTriangle className="h-3.5 w-3.5" /> {withheld} withheld (failed validation)</span>
        )}
        {skipped > 0 && (
          <span className="inline-flex items-center gap-1.5"><XCircle className="h-3.5 w-3.5 text-zinc-400" /> {skipped} skipped (too uncertain)</span>
        )}
        {withheld === 0 && renderable === total - skipped && total - skipped > 0 && (
          <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> all confident components passed</span>
        )}
      </div>
    </div>
  );
};
